import { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Filter, Plus, Loader2, ExternalLink, Sparkles, AlertTriangle, CalendarRange, ArrowDownToLine, ArrowUpFromLine, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { unwrapArray } from "../../lib/api-envelope";
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { customerColorsInView, generateDays } from "../../data/rentalDispatchData";
import type { GanttAsset, GanttBar, GanttCategory } from "../../data/rentalDispatchData";
import { api, getApiErrorMessage } from "../../api/client";
import {
  formatInstant,
  instantTenantDay,
  parseCalendarDate,
  tenantTodayDate,
  todayCalendarDate,
  zonedWallClockToInstant,
} from "../../lib/calendar-date";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { FleetPickerNotice } from "../../components/rentals/FleetPickerNotice";
import { isTrailerUnit } from "../../lib/hauling";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { fmtCurrency, parseMoney } from "../../lib/display-labels";
import { displayValue } from "../../features/fieldiq-handoff/contracts";

/* ─── Types ─── */

interface CustomerRecord {
  id: string;
  name: string;
  company?: string;
}

/**
 * `GET /surface-rentals/orders` returns snake_case (`order_number`,
 * `customer_name`). Reads tolerate both spellings per CLAUDE.md; the camelCase
 * halves are what this screen declared, and alone they made every option in the
 * Order picker render as a bare UUID followed by "Unknown".
 */
interface OrderRecord {
  id: string;
  order_number?: string;
  orderNumber?: string;
  customer?: string;
  customer_name?: string;
  customerName?: string;
  well?: string;
  well_name?: string;
  assets?: string[];
  status?: string;
}

/**
 * `GET /surface-rentals/fleet` returns `{ trucks: [...], drivers: [...] }` —
 * an OBJECT, not an array. The query below tested `Array.isArray(raw?.data)`
 * against it, so `fleet` was `[]` on every successful response and the truck
 * and driver pickers never populated. What rendered instead was the free-text
 * fallback, which is why the payload carried a unit number and a person's name
 * where the handler wanted two UUIDs.
 */
interface FleetTruck {
  id: string;
  unit_number?: string;
  type?: string;
  year?: number | null;
  make?: string | null;
  model?: string | null;
  status?: string;
}

interface FleetDriver {
  id: string;
  first_name?: string;
  last_name?: string;
  status?: string;
}

/**
 * What `GET /surface-rentals/dispatches` ACTUALLY returns: `rental_dispatches.*`
 * plus `order_number` and `customer_name` from the join.
 */
interface DispatchApiRow {
  id: string;
  order_id: string;
  dispatch_type: string;
  status?: string | null;
  scheduled_date?: string | null;
  driver_name?: string | null;
  truck_info?: string | null;
  pickup_address?: string | null;
  delivery_address?: string | null;
  special_instructions?: string | null;
  assets?: string[] | null;
  /**
   * What the movement carries (fieldiq-api-ee, A4): the dispatch's own assets,
   * or the order's lines when the dispatch names none (`source`). Read
   * defensively until it is live; absent, a row is the order.
   */
  dispatch_assets?: DispatchAssetRow[] | null;
  notes?: string | null;
  order_number?: string | null;
  customer_name?: string | null;
}

/** A row of GET /surface-rentals/orders/:id/assets: `rental_order_assets.*` plus the asset's number, name and category. */
interface OrderAssetRow {
  asset_id?: string | null;
  asset_number?: string | null;
  asset_name?: string | null;
  category?: string | null;
}

/**
 * The order-assets read failed. When the API answered with its own reason (a
 * 403, an "Order not found" 404), that sentence is shown as the API wrote it;
 * a timeout or a 5xx with no reason gets the standard unreachable notice. It is
 * never "This order has no assets", which is a claim about the order.
 */
function OrderAssetsFailure({ error }: { error: unknown }) {
  const reason = textOf((error as { response?: { data?: { error?: unknown } } } | null)?.response?.data?.error);
  if (reason) {
    return (
      <p role="status" data-testid="order-assets-refusal" className="text-xs text-fieldiq-text">
        {reason}
      </p>
    );
  }
  return <LoadFailureNotice error={error} subject="this order's assets" />;
}

interface DispatchAssetRow {
  asset_id?: string | null;
  asset_number?: string | null;
  name?: string | null;
  category?: string | null;
  source?: "dispatch" | "order" | string | null;
}

/** One row per asset the movement carries, keyed so the same asset is one row. */
function rowsFor(row: DispatchApiRow): { key: string; name: string; title: string | null }[] {
  if (Array.isArray(row.dispatch_assets) && row.dispatch_assets.length > 0) {
    return row.dispatch_assets.map((a, i) => {
      const number = textOf(a?.asset_number) || null;
      const name = textOf(a?.name) || null;
      return { key: `asset:${a?.asset_id ?? number ?? name ?? i}`, name: number ?? name ?? "—", title: number ? name : null };
    });
  }
  if (Array.isArray(row.assets) && row.assets.length > 0) {
    return row.assets.map((a) => ({ key: `asset:${String(a)}`, name: String(a), title: null }));
  }
  return [];
}

interface AiSuggestion {
  asset_name: string;
  serial_number: string;
  match_score: number;
  daily_rate: number;
  reason: string;
  warnings?: string[];
  is_backup?: boolean;
}

interface AiSuggestResponse {
  suggestions: AiSuggestion[];
  conflict_alerts?: string[];
  suggested_backup?: AiSuggestion;
}

const textOf = (v: unknown): string => (typeof v === "string" ? v : "");

/**
 * POST /dispatch/ai-suggest returns the MODEL's JSON, shaped only by the
 * instruction it was given: `recommendations` (not `suggestions`, which this
 * page read, so a working call would still have shown nothing),
 * `conflict_alerts`, and `suggested_backup` as just `{asset_id, asset_name}`.
 * Nothing guarantees any of it, so each field is read defensively and an
 * absent number stays absent (the cards print a dash for it).
 */
function readAiSuggest(raw: unknown): AiSuggestResponse {
  const body = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const toSuggestion = (r: unknown): AiSuggestion | null => {
    if (!r || typeof r !== "object") return null;
    const o = r as Record<string, unknown>;
    if (!textOf(o.asset_name)) return null;
    return {
      asset_name: textOf(o.asset_name),
      serial_number: textOf(o.serial_number),
      match_score: o.match_score as number,
      daily_rate: o.daily_rate as number,
      reason: textOf(o.reason),
      warnings: Array.isArray(o.warnings) ? o.warnings.filter((w): w is string => typeof w === "string") : [],
    };
  };
  return {
    suggestions: (Array.isArray(body.recommendations) ? body.recommendations : []).map(toSuggestion).filter((x): x is AiSuggestion => !!x),
    conflict_alerts: Array.isArray(body.conflict_alerts) ? body.conflict_alerts.filter((a): a is string => typeof a === "string") : [],
    suggested_backup: toSuggestion(body.suggested_backup) ?? undefined,
  };
}

/* ─── Constants ─── */

const TOTAL_DAYS = 14;

const DISPATCH_TYPE_LABELS: Record<string, string> = {
  delivery: "Deliveries",
  pickup: "Pickups",
  transfer: "Transfers",
  swap: "Swaps",
  relocation: "Relocations",
};
const DISPATCH_TYPE_ONE: Record<string, string> = {
  delivery: "Delivery", pickup: "Pickup", transfer: "Transfer", swap: "Swap", relocation: "Relocation",
};

/**
 * `rental_dispatches.status` is free text. The API writes `pending` (the
 * default) and the field delivery and return screens write `completed`.
 * Anything else is a dash. The bar popover used to say "Active" for every
 * dispatch, whatever its status.
 */
const DISPATCH_STATUS: Record<string, { label: string; fill: string }> = {
  pending: { label: "Pending", fill: "bg-fi-signal-amber" },
  completed: { label: "Completed", fill: "bg-fi-signal-green" },
};
const statusSkin = (s: string | null) =>
  s != null && Object.prototype.hasOwnProperty.call(DISPATCH_STATUS, s) ? DISPATCH_STATUS[s] : { label: "—", fill: "bg-fi-signal-slate" };


const badge = "inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.62rem] font-black uppercase leading-none text-fi-signal-ink";
const selectCls = "h-9 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none";
const bandSecondary = "fi-action fi-action--standard border border-white/25 hover:border-white/60";

/**
 * Turn the dispatch rows the API returns into the shape this board draws:
 * one row per asset the dispatch carries, headed by the asset (FT-007) with the
 * order under it; a dispatch that names no asset is one row headed by its order,
 * said once (it read "RO-2026-00006 / RO-2026-00006"). One single-day bar on
 * the scheduled day: a dispatch is a movement on a date, not a rental period.
 */
function dispatchesToGantt(rows: DispatchApiRow[], startDate: Date, timeZone: string | null): GanttCategory[] {
  const startMs = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()).getTime();
  const byType = new Map<string, GanttCategory>();
  const rowByKey = new Map<string, GanttAsset>();

  for (const row of rows) {
    if (!row.scheduled_date) continue;
    // `scheduled_date` is a timestamptz placed on a calendar day: the TENANT's
    // day the instant fell on (companies.time_zone), built locally once it is
    // a day. Its UTC date prefix put an Alberta evening on the next column.
    const scheduled = parseCalendarDate(instantTenantDay(row.scheduled_date, timeZone));
    if (!scheduled) continue;
    const startDay = Math.round((scheduled.getTime() - startMs) / 86_400_000);
    if (startDay < 0 || startDay >= TOTAL_DAYS) continue;

    const typeKey = (row.dispatch_type || "").toLowerCase();
    const label = DISPATCH_TYPE_LABELS[typeKey] ?? "Other movements";
    let category = byType.get(label);
    if (!category) {
      category = { label, assets: [] };
      byType.set(label, category);
    }

    const customer = row.customer_name ?? "";
    const bar: GanttBar = {
      customer,
      location: row.delivery_address ?? row.pickup_address ?? "",
      colorClass: customer,
      startDay,
      spanDays: 1,
      dispatchType: typeKey || null,
      status: row.status ?? null,
      scheduledAt: row.scheduled_date ?? null,
      driver: row.driver_name ?? null,
      truck: row.truck_info ?? null,
      orderNumber: row.order_number ?? null,
      dispatchId: row.id ?? null,
      orderId: row.order_id ?? null,
    };

    const assets = rowsFor(row);
    const rows: { key: string; make: () => GanttAsset }[] = assets.length > 0
      ? assets.map((a) => ({ key: `${a.key}|${row.order_number ?? ""}`, make: () => ({ name: a.name, title: a.title, id: row.order_number ?? null, bars: [] }) }))
      : [{ key: `order:${row.order_id ?? row.order_number ?? row.id}`, make: () => ({ name: row.order_number ?? "—", id: null, bars: [] }) }];
    for (const r of rows) {
      // Per category: an asset delivered and later picked up is a row under each.
      const key = `${label}|${r.key}`;
      let asset = rowByKey.get(key);
      if (!asset) {
        asset = r.make();
        rowByKey.set(key, asset);
        category.assets.push(asset);
      }
      asset.bars.push(bar);
    }
  }

  return Array.from(byType.values());
}

/* ─── Loading Skeleton ─── */

function GanttSkeleton() {
  return (
    <div className="min-w-[900px] animate-pulse" aria-hidden="true">
      {Array.from({ length: 6 }).map((_, ri) => (
        <div key={ri} className="flex min-h-[44px] items-center border-b border-fieldiq-border">
          <div className="w-[200px] min-w-[200px] shrink-0 border-r border-fieldiq-border px-4 py-2">
            <div className="mb-1 h-3 w-28 rounded bg-fieldiq-dark/60" />
          </div>
          <div className="relative flex-1" style={{ minHeight: 44 }} />
        </div>
      ))}
    </div>
  );
}

/* ─── Main Component ─── */

/**
 * The Dispatch Board: rental deliveries, pickups and transfers on a two-week
 * timeline. It was titled "Rentals Dispatch", the same heading as /rentals.
 */
const RentalDispatchBoard = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [weekOffset, setWeekOffset] = useState(0);
  /**
   * The customers the reader has HIDDEN. It was the set they had SELECTED,
   * seeded once the customer list loaded, so until then every bar was hidden,
   * and a dispatch on an order with no customer was hidden forever. Empty now
   * means "show everything", and a bar with no customer is always shown.
   */
  const [hiddenCustomers, setHiddenCustomers] = useState<Set<string>>(() => new Set());
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // AI Suggest state
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<AiSuggestResponse | null>(null);

  // Schedule form state
  const [formType, setFormType] = useState<"Delivery" | "Pickup" | "Transfer">("Delivery");
  const [formOrder, setFormOrder] = useState("");
  const [formAssets, setFormAssets] = useState<string[]>([]);
  const [formTruck, setFormTruck] = useState("");
  const [formTrailer, setFormTrailer] = useState("");
  const [formDriver, setFormDriver] = useState("");
  const [formDate, setFormDate] = useState("");
  /** The equipment category to suggest from: the one thing the AI route requires. */
  const [aiAssetType, setAiAssetType] = useState("");
  const [formTime, setFormTime] = useState("");
  const [formNotes, setFormNotes] = useState("");

  /* ─── Data Fetching ─── */

  const dispatchQuery = useQuery({
    queryKey: ["rentals-dispatch"],
    queryFn: async () => {
      const res = await api.rentals.dispatch.list();
      return unwrapArray<DispatchApiRow>(res.data);
    },
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failed = queryLoadFailure(dispatchQuery) != null;
  const loading = dispatchQuery.isLoading && !failed;
  const dispatchRows = failed ? undefined : dispatchQuery.data;
  /**
   * The dispatches that will run. A cancelled one (fe#815 cancel — its order
   * was cancelled, or it was) holds no asset on its day and is not work to do,
   * so it is off the timeline and out of every band figure, as the trucking
   * calendar leaves out a cancelled load. Its order page still lists it,
   * labelled Cancelled.
   */
  const liveRows = useMemo(
    () => (dispatchRows ? dispatchRows.filter((r) => r.status !== "cancelled") : undefined),
    [dispatchRows],
  );

  // The rental equipment categories, for AI Suggest's required `asset_type`.
  // The key is shared with the asset registry and Manage Categories, which
  // cache the category ROWS. Storing the names here put strings where they
  // read objects, and objects where this reads strings (rendered as a React
  // child, which throws). So the cache holds the rows and `select` derives the
  // names for this observer only.
  const { data: aiCategories = [] } = useQuery({
    queryKey: ["rentals", "asset-categories"],
    queryFn: () => api.rentals.assetCategories.list().then((r) => unwrapArray<Record<string, unknown>>(r.data)),
    select: (rows: Record<string, unknown>[]) =>
      rows.filter((c) => c.active !== false && typeof c.name === "string").map((c) => String(c.name)),
  });
  const { data: customersRaw } = useQuery({
    queryKey: ["rentals-customers"],
    queryFn: () => api.customers.list().then((r) => unwrapArray<CustomerRecord>(r.data)),
    staleTime: 60_000,
  });
  const customers = useMemo(() => customersRaw ?? [], [customersRaw]);

  const { data: ordersRaw } = useQuery({
    queryKey: ["rentals-dispatch-orders"],
    queryFn: () => api.rentals.orders.list().then((r) => unwrapArray<OrderRecord>(r.data)),
    staleTime: 30_000,
  });
  const orders = ordersRaw ?? [];

  const fleetQuery = useQuery<{ trucks: FleetTruck[]; drivers: FleetDriver[] }>({
    queryKey: ["rentals-fleet"],
    queryFn: () =>
      api.rentals.fleet().then((r) => {
        const body = r.data;
        const payload = body && typeof body === "object" && "data" in body ? body.data : body;
        return {
          trucks: Array.isArray(payload?.trucks) ? payload.trucks : [],
          drivers: Array.isArray(payload?.drivers) ? payload.drivers : [],
        };
      }),
    staleTime: 60_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const fleetRaw = fleetQuery.data;
  const fleetFailed = queryLoadFailure(fleetQuery) != null;
  // Trucks and trailers come back as one list; a trailer is pulled, not driven
  // (api#1045). Split by the API's own `?type=trailer` rule.
  const fleetUnits = fleetRaw?.trucks ?? [];
  const fleet = fleetUnits.filter((f) => !isTrailerUnit(f.type));
  const trailers = fleetUnits.filter((f) => isTrailerUnit(f.type));

  /** The tenant's own customers. No fixture fallback. */
  const customerFilterItems = useMemo(
    () => customers.map((c) => ({ label: c.company || c.name, key: c.company || c.name })),
    [customers],
  );

  /** The drivers the API returned, with their real `trucking_drivers` ids. */
  const drivers = useMemo(
    () =>
      (fleetRaw?.drivers ?? []).map((d) => ({
        id: d.id,
        name: [d.first_name, d.last_name].filter(Boolean).join(" ") || "Driver",
      })),
    [fleetRaw],
  );

  /**
   * The order's assets, from GET /surface-rentals/orders/:id/assets. This read
   * `order.assets` off the ORDERS LIST, which carries no such field (only an
   * `asset_count`), so the picker was empty on every order and the dispatcher
   * typed asset names by hand. The value sent is the asset id, which the
   * dispatch reads resolve to the asset (api#1096 `dispatch_assets`).
   */
  const orderAssetsQuery = useQuery({
    queryKey: ["rentals-order-assets", formOrder],
    queryFn: () => api.rentals.orders.getAssets(formOrder).then((r) => unwrapArray<OrderAssetRow>(r.data)),
    enabled: !!formOrder,
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const orderAssetsFailed = queryLoadFailure(orderAssetsQuery) != null;
  const orderAssets: { id: string; label: string }[] = useMemo(
    () =>
      (orderAssetsFailed ? [] : orderAssetsQuery.data ?? [])
        .filter((a) => textOf(a?.asset_id))
        .map((a) => {
          const number = textOf(a.asset_number);
          const name = textOf(a.asset_name);
          return { id: String(a.asset_id), label: [number, name].filter(Boolean).join(" — ") || "—" };
        }),
    [orderAssetsQuery.data, orderAssetsFailed],
  );

  /* ─── Date Logic ─── */

  // The board's weeks are the TENANT's weeks: today, and so this Monday, is
  // read in companies.time_zone, not on the viewer's device. With no zone
  // there is no honest today, so there is no window: no days, and the range
  // reads "—" rather than anchoring on the device's week.
  const timeZone = useTenantTimeZone();
  const startDate = useMemo(() => {
    const monday = tenantTodayDate(timeZone); // a fresh local-midnight Date
    if (!monday) return null;
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7) + weekOffset * 14);
    return monday;
  }, [weekOffset, timeZone]);

  const days = useMemo(
    () => (startDate ? generateDays(startDate, TOTAL_DAYS, todayCalendarDate(timeZone)) : []),
    [startDate, timeZone],
  );
  const ganttData = useMemo(
    () => (startDate ? dispatchesToGantt(liveRows ?? [], startDate, timeZone) : []),
    [liveRows, startDate, timeZone],
  );

  /**
   * The customers with a movement in the two weeks on screen, by name. The
   * legend lists these and nobody else: it listed every customer the tenant
   * has (nine on Apex Surface Rentals, eight with nothing in view, some who
   * never rent), which on a phone was six lines above one bar. The colours
   * are dealt from the same list, so the customers in view are told apart.
   */
  const customersInView = useMemo(() => {
    const names = new Set<string>();
    for (const cat of ganttData) for (const a of cat.assets) for (const b of a.bars) if (b.customer) names.add(b.customer);
    return Array.from(names).sort((x, y) => x.localeCompare(y));
  }, [ganttData]);
  const colorFor = useMemo(() => customerColorsInView(customersInView), [customersInView]);

  const endDate = useMemo(() => {
    if (!startDate) return null;
    const d = new Date(startDate);
    d.setDate(d.getDate() + 13);
    return d;
  }, [startDate]);

  const formatRange = () => {
    if (!startDate || !endDate) return "—";
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[startDate.getMonth()]} ${startDate.getDate()} – ${months[endDate.getMonth()]} ${endDate.getDate()}, ${endDate.getFullYear()}`;
  };

  /**
   * Band figures: the dispatches in the two weeks on screen, counted from the
   * loaded list (GET /dispatches is unpaginated; nothing returns these counts).
   * One dispatch is counted once, however many asset rows it draws.
   */
  const inWindow = useMemo(() => {
    if (!startDate) return [];
    const startMs = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()).getTime();
    return (liveRows ?? []).filter((r) => {
      const d = parseCalendarDate(instantTenantDay(r.scheduled_date, timeZone));
      if (!d) return false;
      const day = Math.round((d.getTime() - startMs) / 86_400_000);
      return day >= 0 && day < TOTAL_DAYS;
    });
  }, [liveRows, startDate, timeZone]);
  const tally = (n: number) => (failed ? "—" : displayValue(dispatchRows ? n : null));

  /* ─── Handlers ─── */

  const toggleCustomer = useCallback((customer: string) => {
    setHiddenCustomers((prev) => {
      const next = new Set(prev);
      if (next.has(customer)) next.delete(customer);
      else next.add(customer);
      return next;
    });
  }, []);

  const resetForm = () => {
    setFormType("Delivery");
    setFormOrder("");
    setFormAssets([]);
    setFormTruck("");
    setFormTrailer("");
    setFormDriver("");
    setFormDate("");
    setFormTime("");
    setFormNotes("");
  };

  const toggleAssetSelection = (asset: string) => {
    setFormAssets((prev) => (prev.includes(asset) ? prev.filter((a) => a !== asset) : [...prev, asset]));
  };

  /**
   * POST /dispatch/ai-suggest reads `asset_type` (required, matched against
   * the asset category), `job_type`, `start_date`, `end_date`,
   * `wellsite_lsd` and `required_specs` (#815). It used to send `order_id`,
   * `dispatch_type`, `date` and `assets`, none of which it reads, so every
   * press was a 400.
   */
  const handleAiSuggest = async () => {
    if (!aiAssetType) {
      toast.error("Choose an equipment type for AI Suggest.");
      return;
    }
    setAiSuggesting(true);
    setAiSuggestions(null);
    try {
      const res = await api.rentals.ai.dispatchSuggest({
        asset_type: aiAssetType,
        job_type: formType.toLowerCase(),
        ...(formDate ? { start_date: formDate } : {}),
      });
      setAiSuggestions(readAiSuggest(res.data?.data));
    } catch (err) {
      toast.error(getApiErrorMessage(err, "AI suggestions unavailable"));
    } finally {
      setAiSuggesting(false);
    }
  };

  const selectAiSuggestion = (suggestion: AiSuggestion) => {
    const assetLabel = `${suggestion.asset_name} ${suggestion.serial_number}`;
    setFormAssets((prev) => (prev.includes(assetLabel) ? prev : [...prev, assetLabel]));
    toast(`Selected ${suggestion.asset_name}`);
  };

  const handleScheduleSubmit = async () => {
    if (!formOrder) {
      toast.error("Please select an order.");
      return;
    }
    if (!formDate || !formTime) {
      toast.error("Please fill in date and time.");
      return;
    }

    setSubmitting(true);
    try {
      // `scheduled_date` is one timestamptz. The form collects a calendar day
      // and a wall-clock time on the TENANT's clock (companies.time_zone), so
      // 06:00 means 06:00 where the tenant is — not 06:00 UTC, and not 06:00
      // wherever the dispatcher's laptop happens to be.
      const [y, m, d] = formDate.split("-").map(Number);
      const [hh, mm] = formTime.split(":").map(Number);
      const scheduledAt = zonedWallClockToInstant(y, m, d, hh || 0, mm || 0, timeZone);
      if (!scheduledAt) {
        toast.error("Could not read the date and time in your company's time zone.");
        return;
      }
      const scheduledDate = scheduledAt.toISOString();

      const payload = {
        order_id: formOrder,
        dispatch_type: formType.toLowerCase(),
        scheduled_date: scheduledDate,
        assets: formAssets.length > 0 ? formAssets : undefined,
        // Ids only (#815): the pickers offer no free text. The handler resolves
        // `truck_id` / `driver_id` against trucking_fleet / trucking_drivers and
        // stores the unit and driver labels itself.
        truck_id: formTruck || undefined,
        trailer_id: formTrailer || undefined,
        driver_id: formDriver || undefined,
        notes: formNotes || undefined,
      };

      await api.rentals.dispatch.create(payload);
      toast.success(`${formType} dispatch scheduled successfully.`);
      resetForm();
      setScheduleOpen(false);
      queryClient.invalidateQueries({ queryKey: ["rentals-dispatch"] });
    } catch (err) {
      // The dialog stays open with the form intact. Nothing is added to the
      // board: a dispatch the server refused is not a dispatch.
      toast.error(getApiErrorMessage(err, "Failed to schedule dispatch"));
    } finally {
      setSubmitting(false);
    }
  };

  const allCustomerKeys = useMemo(() => customerFilterItems.map((c) => c.key), [customerFilterItems]);
  const shownCount = allCustomerKeys.filter((k) => !hiddenCustomers.has(k)).length;

  return (
    <div data-testid="rental-dispatch-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Surface rentals"
        title="Dispatch Board"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className={bandSecondary}>
                  <Filter className="size-4" aria-hidden="true" />
                  Customers
                  {hiddenCustomers.size > 0 && <span className="tabular-nums">{shownCount}/{allCustomerKeys.length}</span>}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-60 border border-fieldiq-border bg-fieldiq-card p-3 shadow-lg" align="end">
                <p className="mb-2 text-xs font-semibold text-fieldiq-muted">Show customers</p>
                {customerFilterItems.length === 0 ? (
                  <p className="text-xs text-fieldiq-muted">No customers</p>
                ) : (
                  <div className="space-y-2">
                    {customerFilterItems.map((item) => {
                      const c = colorFor(item.key);
                      return (
                        <label key={item.key} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 hover:bg-fieldiq-dark/50">
                          <Checkbox checked={!hiddenCustomers.has(item.key)} onCheckedChange={() => toggleCustomer(item.key)} />
                          <span className={`h-2.5 w-2.5 rounded-sm border border-black/20 ${c.dot}`} aria-hidden="true" />
                          <span className="text-xs text-fieldiq-text">{item.label}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <div className="mt-3 flex gap-2 border-t border-fieldiq-border pt-2">
                  <button type="button" className="h-7 flex-1 rounded-fi-control text-[11px] font-bold text-fieldiq-text hover:bg-fieldiq-dark/50" onClick={() => setHiddenCustomers(new Set())}>
                    Show all
                  </button>
                  <button type="button" className="h-7 flex-1 rounded-fi-control text-[11px] font-bold text-fieldiq-text hover:bg-fieldiq-dark/50" onClick={() => setHiddenCustomers(new Set(allCustomerKeys))}>
                    Hide all
                  </button>
                </div>
              </PopoverContent>
            </Popover>
            <button type="button" onClick={() => setScheduleOpen(true)} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              Schedule Dispatch
            </button>
          </div>
        }
        stats={[
          { icon: <CalendarRange className="size-4" aria-hidden="true" />, label: "Scheduled", sub: "These two weeks", tone: "neutral", loading, value: tally(inWindow.length) },
          { icon: <ArrowDownToLine className="size-4" aria-hidden="true" />, label: "Deliveries", sub: "To site", tone: "info", loading, value: tally(inWindow.filter((r) => (r.dispatch_type || "").toLowerCase() === "delivery").length) },
          { icon: <ArrowUpFromLine className="size-4" aria-hidden="true" />, label: "Pickups", sub: "Back to yard", tone: "success", loading, value: tally(inWindow.filter((r) => (r.dispatch_type || "").toLowerCase() === "pickup").length) },
          { icon: <Clock className="size-4" aria-hidden="true" />, label: "Pending", sub: "Not completed", tone: "accent", loading, value: tally(inWindow.filter((r) => r.status === "pending").length) },
        ]}
      />

      {/* Schedule Dispatch Dialog */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Schedule Dispatch</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="dispatch-type">Type</Label>
              <select id="dispatch-type" value={formType} onChange={(e) => setFormType(e.target.value as "Delivery" | "Pickup" | "Transfer")} className={selectCls}>
                <option value="Delivery">Delivery</option>
                <option value="Pickup">Pickup</option>
                <option value="Transfer">Transfer</option>
              </select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="dispatch-order">Order *</Label>
              {orders.length > 0 ? (
                <select id="dispatch-order" value={formOrder} onChange={(e) => { setFormOrder(e.target.value); setFormAssets([]); }} className={selectCls}>
                  <option value="">Select order</option>
                  {orders.map((order) => (
                    <option key={order.id} value={order.id}>
                      {order.order_number ?? order.orderNumber ?? order.id} — {order.customer_name ?? order.customerName ?? order.customer ?? "—"}
                    </option>
                  ))}
                </select>
              ) : (
                <Input id="dispatch-order" placeholder="No orders loaded — enter order ID" value={formOrder} onChange={(e) => setFormOrder(e.target.value)} />
              )}
            </div>

            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label>Asset(s)</Label>
                <Button type="button" variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={handleAiSuggest} disabled={aiSuggesting || !aiAssetType}>
                  {aiSuggesting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                  AI Suggest
                </Button>
              </div>
              {!formOrder ? (
                <p className="text-xs text-fieldiq-muted">Choose an order to list its assets.</p>
              ) : orderAssetsFailed ? (
                <OrderAssetsFailure error={queryFailureError(orderAssetsQuery)} />
              ) : orderAssetsQuery.isLoading ? (
                <p className="text-xs text-fieldiq-muted">Loading the order&apos;s assets…</p>
              ) : orderAssets.length > 0 ? (
                <div data-testid="order-asset-picker" className="max-h-[120px] space-y-1.5 overflow-y-auto rounded-fi-control border border-fieldiq-border p-2">
                  {orderAssets.map((asset) => (
                    <label key={asset.id} className="flex cursor-pointer items-center gap-2">
                      <Checkbox checked={formAssets.includes(asset.id)} onCheckedChange={() => toggleAssetSelection(asset.id)} />
                      <span className="text-xs text-fieldiq-text">{asset.label}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-fieldiq-muted">This order has no assets on it.</p>
              )}
              {formAssets.length > 0 && <p className="text-[10px] text-fieldiq-muted">{formAssets.length} asset(s) selected</p>}
              <div className="grid gap-1">
                <Label htmlFor="ai-asset-type" className="text-[10px] text-fieldiq-muted">Equipment type for AI Suggest</Label>
                <select id="ai-asset-type" value={aiAssetType} onChange={(e) => setAiAssetType(e.target.value)} className={selectCls}>
                  <option value="">Choose a type…</option>
                  {aiCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {aiSuggesting && (
                <div className="flex items-center gap-2 py-2 text-sm text-fieldiq-muted">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Finding best available units...
                </div>
              )}

              {aiSuggestions && (
                <div className="mt-1 space-y-2">
                  {aiSuggestions.conflict_alerts && aiSuggestions.conflict_alerts.length > 0 && (
                    <div className="flex items-start gap-2 rounded-fi-control border border-fi-amber/40 bg-fi-amber/10 px-3 py-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-fieldiq-text" />
                      <div className="space-y-0.5 text-xs text-fieldiq-text">
                        {aiSuggestions.conflict_alerts.map((alert, i) => <p key={i}>{alert}</p>)}
                      </div>
                    </div>
                  )}
                  {(aiSuggestions.suggestions ?? []).map((s, idx) => (
                    <button key={idx} type="button" onClick={() => selectAiSuggestion(s)} className="w-full rounded-fi-control border border-fieldiq-border p-3 text-left transition-colors hover:bg-fieldiq-dark/50">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-medium text-fieldiq-text">{s.asset_name}</p>
                          <p className="font-mono text-xs text-fieldiq-muted">{s.serial_number}</p>
                        </div>
                        <span className="text-xs font-semibold text-fieldiq-text">{fmtCurrency(parseMoney(s.daily_rate))}/day</span>
                      </div>
                      <p className="mt-1.5 text-xs text-fieldiq-muted">Match {displayValue(parseMoney(s.match_score))}% · {s.reason}</p>
                      {s.warnings && s.warnings.length > 0 && (
                        <div className="mt-1.5 space-y-0.5">
                          {s.warnings.map((w, wi) => (
                            <p key={wi} className="flex items-center gap-1 text-[10px] text-fieldiq-text">
                              <AlertTriangle className="h-3 w-3" />{w}
                            </p>
                          ))}
                        </div>
                      )}
                    </button>
                  ))}
                  {aiSuggestions.suggested_backup && (
                    <div className="rounded-fi-control border border-dashed border-fieldiq-border p-3">
                      <p className="mb-1 text-[10px] font-bold uppercase text-fieldiq-muted">Backup Option</p>
                      <button type="button" onClick={() => selectAiSuggestion(aiSuggestions.suggested_backup!)} className="w-full text-left">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm font-medium text-fieldiq-text">{aiSuggestions.suggested_backup.asset_name}</p>
                            <p className="font-mono text-xs text-fieldiq-muted">{aiSuggestions.suggested_backup.serial_number}</p>
                          </div>
                          <span className="text-xs font-semibold text-fieldiq-muted">{fmtCurrency(parseMoney(aiSuggestions.suggested_backup.daily_rate))}/day</span>
                        </div>
                        <p className="mt-1 text-xs text-fieldiq-muted">{aiSuggestions.suggested_backup.reason}</p>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid gap-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-2">
                  <Label htmlFor="dispatch-truck">Truck</Label>
                  {/* The VALUE is the trucking_fleet id, not the unit number: the
                      handler resolves `truck_id` against that table. */}
                  <select id="dispatch-truck" value={formTruck} onChange={(e) => setFormTruck(e.target.value)} className={selectCls} disabled={fleet.length === 0}>
                    <option value="">{fleet.length > 0 ? "Select truck" : "None set up"}</option>
                    {fleet.map((f) => <option key={f.id} value={f.id}>{f.unit_number || f.id} — {f.type || "Truck"}</option>)}
                  </select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="dispatch-trailer">Trailer</Label>
                  <select id="dispatch-trailer" value={formTrailer} onChange={(e) => setFormTrailer(e.target.value)} className={selectCls} disabled={trailers.length === 0}>
                    <option value="">{trailers.length > 0 ? "No trailer" : "None set up"}</option>
                    {trailers.map((f) => <option key={f.id} value={f.id}>{f.unit_number || f.id}</option>)}
                  </select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="dispatch-driver">Driver</Label>
                  <select id="dispatch-driver" value={formDriver} onChange={(e) => setFormDriver(e.target.value)} className={selectCls} disabled={drivers.length === 0}>
                    <option value="">{drivers.length > 0 ? "Select driver" : "None set up"}</option>
                    {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              </div>
              {fleetRaw || fleetFailed ? <FleetPickerNotice failed={fleetFailed} trucks={fleet.length} drivers={drivers.length} /> : null}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="dispatch-date">Date</Label>
                <Input id="dispatch-date" type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)} />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="dispatch-time">Time</Label>
                <Input id="dispatch-time" type="time" value={formTime} onChange={(e) => setFormTime(e.target.value)} />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="dispatch-notes">Notes</Label>
              <Textarea id="dispatch-notes" placeholder="Any additional notes..." value={formNotes} onChange={(e) => setFormNotes(e.target.value)} rows={3} />
            </div>

            <button type="button" onClick={handleScheduleSubmit} className="fi-action fi-action--standard fi-action--primary mt-2 justify-center disabled:opacity-50" disabled={submitting}>
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Schedule Dispatch
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* Week navigation */}
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" aria-label="Previous two weeks" onClick={() => setWeekOffset((p) => p - 1)} className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card p-1.5 text-fieldiq-text hover:bg-fieldiq-dark/50">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[170px] text-center text-sm font-bold text-fieldiq-text">{formatRange()}</span>
            <button type="button" aria-label="Next two weeks" onClick={() => setWeekOffset((p) => p + 1)} className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card p-1.5 text-fieldiq-text hover:bg-fieldiq-dark/50">
              <ChevronRight className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => setWeekOffset(0)} className="h-8 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 text-xs font-bold text-fieldiq-text hover:bg-fieldiq-dark/50">
              Today
            </button>
            {/* Legend: the customers with a movement in view, in the colours drawn. */}
            {customersInView.length > 0 && (
              <ul data-testid="dispatch-legend" aria-label="Customers in view" className="ml-auto flex flex-wrap items-center gap-3">
                {customersInView.map((name) => (
                  <li key={name} className="flex items-center gap-1.5 text-[11px] text-fieldiq-muted">
                    <span data-testid="legend-swatch" className={`h-2.5 w-2.5 rounded-sm border border-black/20 ${colorFor(name).dot}`} aria-hidden="true" />
                    {name}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <section aria-labelledby="dispatch-board-title" className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Two-week timeline</p>
              <h2 id="dispatch-board-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">Movements by asset</h2>
            </header>
            <div className="overflow-auto">
              {failed ? (
                <LoadFailureNotice error={queryFailureError(dispatchQuery)} subject="dispatches" />
              ) : loading ? (
                <GanttSkeleton />
              ) : (
                <GanttView days={days} hiddenCustomers={hiddenCustomers} categories={ganttData} colorFor={colorFor} />
              )}
            </div>
          </section>
        </div>
      </section>
    </div>
  );
};

/** Gap between the bar and its detail, and the detail's minimum distance from the viewport edge. */
const DETAIL_GAP_PX = 4;
const DETAIL_EDGE_PX = 8;

/* ─── Bar detail: what the dispatch row says, and nothing else ─── */
/*
 * Portalled to document.body with fixed positioning (the tooltip's pattern),
 * because the timeline panel is overflow-hidden and its body overflow-auto:
 * drawn inside them, a board with two asset rows cut the detail off at the
 * panel's edge and its Open dispatch / Open order links were unreachable
 * (#815). Below the bar by default; above it when there is no room below.
 * A fixed box would drift off its bar on scroll, so scroll and resize close it.
 * Focus moves into it on open and Escape hands focus back to the bar, since a
 * portal is no longer next to the bar in the tab order.
 */
function BarDetailPopover({ bar, anchor, onClose }: { bar: GanttBar; anchor: HTMLElement | null; onClose: (refocus: boolean) => void }) {
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();
  const skin = statusSkin(bar.status);
  const boxRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!anchor || !boxRef.current) return;
    const a = anchor.getBoundingClientRect();
    const b = boxRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    let top = a.bottom + DETAIL_GAP_PX;
    if (top + b.height > vh - DETAIL_EDGE_PX) top = Math.max(DETAIL_EDGE_PX, a.top - b.height - DETAIL_GAP_PX);
    const left = Math.max(DETAIL_EDGE_PX, Math.min(a.left, vw - b.width - DETAIL_EDGE_PX));
    setPos({ top, left });
  }, [anchor, bar]);

  useEffect(() => {
    boxRef.current?.focus({ preventScroll: true });
    const close = () => onClose(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose(true);
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={boxRef}
      data-testid="dispatch-bar-detail"
      role="dialog"
      aria-label={`${bar.customer || "No customer"} dispatch`}
      tabIndex={-1}
      className="fixed z-50 min-w-[240px] rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-3 text-xs shadow-lg outline-none"
      // Measured before it is shown, so it never flashes at the corner.
      style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: "hidden" }}
      onMouseLeave={() => onClose(false)}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-fieldiq-text">{bar.customer || "—"}</span>
        <span className={`${badge} ${skin.fill}`}>{skin.label}</span>
      </div>
      <dl className="space-y-1 text-fieldiq-muted">
        <div><dt className="inline font-medium text-fieldiq-text">Type: </dt><dd className="inline">{(bar.dispatchType && DISPATCH_TYPE_ONE[bar.dispatchType]) || "—"}</dd></div>
        <div><dt className="inline font-medium text-fieldiq-text">When: </dt><dd className="inline">{formatInstant(bar.scheduledAt, timeZone) ?? "—"}</dd></div>
        <div><dt className="inline font-medium text-fieldiq-text">Where: </dt><dd className="inline">{bar.location || "—"}</dd></div>
        <div><dt className="inline font-medium text-fieldiq-text">Order: </dt><dd className="inline font-mono">{bar.orderNumber ?? "—"}</dd></div>
        <div><dt className="inline font-medium text-fieldiq-text">Driver / truck: </dt><dd className="inline">{bar.driver ?? "—"} · {bar.truck ?? "—"}</dd></div>
      </dl>
      <div className="mt-2 flex flex-wrap gap-3 border-t border-fieldiq-border pt-2">
        {[
          bar.dispatchId ? { label: "Open dispatch", to: `/rentals/dispatches/${encodeURIComponent(bar.dispatchId)}` } : null,
          bar.orderId ? { label: "Open order", to: `/rentals/orders/${encodeURIComponent(bar.orderId)}` } : { label: "Rental Orders", to: "/rentals/active" },
        ]
          .filter((l): l is { label: string; to: string } => l !== null)
          .map((l) => (
            <button
              key={l.label}
              type="button"
              className="flex items-center gap-1 text-[11px] font-bold text-fieldiq-text underline decoration-fi-amber decoration-2 underline-offset-4"
              onClick={(e) => {
                e.stopPropagation();
                navigate(l.to);
              }}
            >
              <ExternalLink className="h-3 w-3" />
              {l.label}
            </button>
          ))}
      </div>
    </div>,
    document.body,
  );
}

/* ─── Gantt View ─── */
function GanttView({
  days,
  hiddenCustomers,
  categories,
  colorFor,
}: {
  days: { dayName: string; date: string; isToday: boolean }[];
  hiddenCustomers: Set<string>;
  categories: GanttCategory[];
  /** The board's colours for the customers in view, so a bar matches its legend swatch. */
  colorFor: ReturnType<typeof customerColorsInView>;
}) {
  const [activeBar, setActiveBar] = useState<{ categoryIdx: number; assetIdx: number; barIdx: number } | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const closeDetail = useCallback(
    (refocus: boolean) => {
      setActiveBar(null);
      if (refocus) anchor?.querySelector("button")?.focus();
    },
    [anchor],
  );

  if (categories.length === 0) {
    return <p className="px-4 py-12 text-center text-sm text-fieldiq-muted">Nothing scheduled in these two weeks</p>;
  }

  return (
    <div className="min-w-[900px]">
      <div className="sticky top-0 z-10 flex border-b border-fieldiq-border bg-fieldiq-card">
        <div className="w-[200px] min-w-[200px] shrink-0 border-r border-fieldiq-border px-4 py-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-fieldiq-muted">Asset</span>
        </div>
        <div className="flex flex-1">
          {days.map((day, i) => (
            <div key={i} className={`min-w-[60px] flex-1 border-r border-fieldiq-border/50 py-2 text-center ${day.isToday ? "bg-fi-amber/10" : ""}`}>
              <p className="text-[11px] font-semibold text-fieldiq-text">{day.dayName}</p>
              <p className="mt-0.5 text-[9px] text-fieldiq-muted">{day.date}</p>
            </div>
          ))}
        </div>
      </div>

      {categories.map((cat, ci) => (
        <div key={ci}>
          <div className="flex border-b border-fieldiq-border bg-fieldiq-dark/40">
            <div className="w-[200px] min-w-[200px] shrink-0 border-r border-fieldiq-border px-4 py-1.5">
              <span className="text-[9px] font-semibold uppercase tracking-widest text-fieldiq-muted">{cat.label}</span>
            </div>
            <div className="flex-1" />
          </div>

          {(cat.assets || []).map((asset, ai) => {
            // A bar with no customer is always shown: hiding it would hide a dispatch nobody can un-hide.
            const filteredBars = (asset.bars || []).filter((bar) => !bar.colorClass || !hiddenCustomers.has(bar.colorClass));
            return (
              <div key={ai} data-testid="gantt-row" className="flex min-h-[44px] items-center border-b border-fieldiq-border/60">
                <div className="w-[200px] min-w-[200px] shrink-0 border-r border-fieldiq-border px-4 py-2">
                  <p data-testid="gantt-row-asset" className="truncate text-xs font-medium text-fieldiq-text" title={asset.title ?? undefined}>{asset.name}</p>
                  {/* The order under the asset. A row that IS the order says it once. */}
                  {asset.id && <p data-testid="gantt-row-order" className="font-mono text-[10px] text-fieldiq-muted">{asset.id}</p>}
                </div>
                <div className="relative flex flex-1 items-center" style={{ minHeight: 44 }}>
                  {days.map((day, i) => (
                    <div key={i} className={`h-[44px] min-w-[60px] flex-1 border-r border-fieldiq-border/30 ${day.isToday ? "bg-fi-amber/10" : ""}`} />
                  ))}
                  {filteredBars.map((bar, bi) => {
                    const c = colorFor(bar.colorClass);
                    const isActive = activeBar?.categoryIdx === ci && activeBar?.assetIdx === ai && activeBar?.barIdx === bi;
                    return (
                      // The popover holds its own link, so it sits BESIDE the bar's
                      // button, not inside it: a button in a button is invalid.
                      <div
                        key={bi}
                        className="absolute top-[8px] h-[28px]"
                        style={{ left: `${(bar.startDay / TOTAL_DAYS) * 100}%`, width: `${(bar.spanDays / TOTAL_DAYS) * 100}%` }}
                      >
                        <button
                          type="button"
                          data-testid="gantt-bar"
                          aria-expanded={isActive}
                          aria-label={`${bar.customer || "No customer"}, ${(bar.dispatchType && DISPATCH_TYPE_ONE[bar.dispatchType]) || "movement"}, ${statusSkin(bar.status).label}`}
                          className={`flex h-full w-full items-center rounded border px-2 text-[10px] font-bold ${c.bg} ${c.border} ${c.text} ${isActive ? "ring-2 ring-fi-amber" : ""}`}
                          onClick={(e) => {
                            setAnchor(e.currentTarget.parentElement);
                            setActiveBar(isActive ? null : { categoryIdx: ci, assetIdx: ai, barIdx: bi });
                          }}
                        >
                          <span className="truncate">{bar.customer || "—"}</span>
                        </button>
                        {isActive && <BarDetailPopover bar={bar} anchor={anchor} onClose={closeDetail} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default RentalDispatchBoard;
